import type { KeyStrength, MergeBridge, PreviewResolution } from "../lib/approvals";

/**
 * Radial visualization of an N-way merge: the incoming event sits in the
 * middle, each involved visitor on a circle around it, and one edge per
 * visitor labeled with the key(s) that bridge them to the event.
 *
 * Activates only at ≥3 involved visitors — for a 2-way merge the bridges
 * list above already says everything visually.
 */
export function MergeGraph({ preview }: { preview: PreviewResolution }) {
  const visitors = preview.involvedVisitors;
  if (visitors.length < 3) return null;

  const W = 520;
  const H = 320;
  const cx = W / 2;
  const cy = H / 2;
  const radius = Math.min(W, H) / 2 - 60;

  // Pre-compute which bridges link each visitor to the event.
  const bridgesByVisitor = new Map<number, MergeBridge[]>();
  for (const b of preview.mergeBridges) {
    for (const vid of b.visitorIds) {
      if (!bridgesByVisitor.has(vid)) bridgesByVisitor.set(vid, []);
      bridgesByVisitor.get(vid)!.push(b);
    }
  }

  // Place visitors on a circle. Canonical first (top), others clockwise.
  const sorted = [...visitors].sort((a, b) => {
    if (a.isCanonical && !b.isCanonical) return -1;
    if (b.isCanonical && !a.isCanonical) return 1;
    return a.visitorId - b.visitorId;
  });
  const positions = sorted.map((v, i) => {
    const angle = -Math.PI / 2 + (i / sorted.length) * Math.PI * 2;
    return {
      v,
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    };
  });

  return (
    <div className="rounded-md border border-border-primary bg-bg-secondary p-3">
      <p className="mb-2 font-mono text-xs uppercase tracking-wider text-text-tertiary">
        Merge-Graph
      </p>
      <p className="mb-3 text-xs text-text-tertiary">
        Welche Brücke verbindet welchen Visitor mit dem eingehenden Event.
      </p>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        aria-label="Merge-Graph"
      >
        {/* Edges first (so nodes overlay them) */}
        {positions.map(({ v, x, y }) => {
          const edgeBridges = bridgesByVisitor.get(v.visitorId) ?? [];
          // Pick the strongest bridge to color the edge — gives the eye an
          // immediate signal "this merge is supported by an email" vs "only
          // by click-IDs".
          const weakest = pickWeakest(edgeBridges.map((b) => b.strength));
          const stroke = strokeColor(weakest);
          const midX = (cx + x) / 2;
          const midY = (cy + y) / 2;
          return (
            <g key={`e-${v.visitorId}`}>
              <line
                x1={cx}
                y1={cy}
                x2={x}
                y2={y}
                stroke={stroke}
                strokeWidth={2}
                strokeOpacity={0.6}
              />
              {/* label */}
              <g transform={`translate(${midX}, ${midY})`}>
                <rect
                  x={-58}
                  y={-9}
                  width={116}
                  height={18}
                  rx={3}
                  fill="var(--color-bg-elevated, #1c1c1c)"
                  fillOpacity={0.92}
                />
                <text
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="fill-text-secondary font-mono"
                  style={{ fontSize: 9 }}
                >
                  {edgeBridges.map((b) => b.keyType).join(", ")}
                </text>
              </g>
            </g>
          );
        })}

        {/* Event node (center) */}
        <g>
          <circle
            cx={cx}
            cy={cy}
            r={28}
            fill="var(--color-warning-bg, #4a3000)"
            stroke="var(--color-warning, #f59e0b)"
            strokeWidth={2}
          />
          <text
            x={cx}
            y={cy + 1}
            textAnchor="middle"
            dominantBaseline="middle"
            className="fill-text-primary"
            style={{ fontSize: 10, fontWeight: 600 }}
          >
            Event
          </text>
        </g>

        {/* Visitor nodes */}
        {positions.map(({ v, x, y }) => (
          <g key={`n-${v.visitorId}`}>
            <circle
              cx={x}
              cy={y}
              r={26}
              fill={
                v.isCanonical
                  ? "var(--color-accent-subtle, #1a2c4a)"
                  : "var(--color-bg-elevated, #1c1c1c)"
              }
              stroke={
                v.isCanonical
                  ? "var(--color-accent, #4d9fff)"
                  : "var(--color-border-primary, #333)"
              }
              strokeWidth={2}
            />
            <text
              x={x}
              y={y - 2}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-text-primary font-mono"
              style={{ fontSize: 9 }}
            >
              {v.visitorId}
            </text>
            <text
              x={x}
              y={y + 9}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-text-tertiary font-mono"
              style={{ fontSize: 7 }}
            >
              {v.isCanonical ? "canonical" : "merge"}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function pickWeakest(strengths: KeyStrength[]): KeyStrength {
  if (strengths.length === 0) return "medium";
  let weakest: KeyStrength = "strong";
  for (const s of strengths) {
    if (s === "weak") return "weak";
    if (s === "medium" && weakest === "strong") weakest = "medium";
  }
  return weakest;
}

function strokeColor(s: KeyStrength): string {
  switch (s) {
    case "strong":
      return "var(--color-success, #22c55e)";
    case "medium":
      return "var(--color-warning, #f59e0b)";
    case "weak":
      return "var(--color-danger, #ef4444)";
  }
}
